/**
 * Scene video loading: play progressively, warm a blob cache in the background.
 *
 * Playback must NOT wait for a full-file download. `acquireVideo` points the
 * media element at the remote URL immediately (the browser Range-streams it,
 * so first frame needs ~1MB, not the whole file) and resolves as soon as the
 * element exists. A shared background fetch then warms a bounded blob cache
 * so revisiting a scene can play from a local object URL.
 *
 * Cache entries are keyed by canonical blob path (query/fragment stripped),
 * so a SAS token refresh (`?sv=...&sig=...`) never restarts bytes already in
 * flight or cached. The fresh signed URL is always used for playback/fetch;
 * only the key is canonicalized.
 */
interface CachedVideo {
  /** Resolves to the blob object URL once warmed, or null if skipped/failed. Never rejects. */
  ready: Promise<string | null>;
  controller: AbortController;
  objectUrl?: string;
  bytes: number;
  users: number;
}

const videos = new Map<string, CachedVideo>();
const leases = new WeakMap<HTMLVideoElement, CachedVideo>();
const MAX_CACHED_VIDEOS = 6;
const MAX_CACHED_BYTES = 128 * 1024 * 1024;
/**
 * The background blob fetch is only a revisit optimization — never worth a
 * huge download. Larger files keep streaming progressively via Range
 * requests and simply skip the blob cache.
 */
const MAX_BACKGROUND_FILE_BYTES = 50 * 1024 * 1024;
/** Progress/completion is logged (no timers) so slow links are diagnosable. */
const PROGRESS_LOG_BYTES = 4 * 1024 * 1024;
const SLOW_WARN_MS = 15 * 1000;

/** Strip SAS/query/fragment so one blob maps to one cache entry. */
export const canonicalVideoKey = (url: string): string => {
  try {
    const parsed = new URL(url, 'http://localhost');
    return `${parsed.protocol}//${parsed.host.toLowerCase()}${parsed.pathname}`;
  } catch {
    return url.split(/[?#]/)[0];
  }
};

const shortName = (url: string): string =>
  url.split(/[?#]/)[0].split('/').pop() || url.split(/[?#]/)[0];

const trimCache = () => {
  let bytes = [...videos.values()].reduce((total, entry) => total + entry.bytes, 0);
  for (const [key, entry] of videos) {
    if (videos.size <= MAX_CACHED_VIDEOS && bytes <= MAX_CACHED_BYTES) break;
    // Never revoke a blob still used by a renderer or an awaiting acquisition.
    if (entry.users > 0) continue;
    videos.delete(key);
    bytes -= entry.bytes;
    entry.controller.abort();
    if (entry.objectUrl) URL.revokeObjectURL(entry.objectUrl);
  }
};

/** Shared background download: warms entry.objectUrl, never rejects. */
const fetchBlobInBackground = (
  key: string,
  fullUrl: string,
  entry: CachedVideo
): void => {
  const name = shortName(fullUrl);
  entry.ready = (async (): Promise<string | null> => {
    try {
      const startTime = Date.now();
      const response = await fetch(fullUrl, { signal: entry.controller.signal });
      if (!response.ok) throw new Error(`Video download failed (${response.status})`);
      const contentType = response.headers.get('Content-Type')?.split(';')[0].trim().toLowerCase();
      if (contentType && /^(text\/|application\/(json|xml|xhtml\+xml))/.test(contentType)) {
        throw new Error(`Video URL returned ${contentType}, not video data. Check the asset URL and storage response.`);
      }
      const contentLength = Number(response.headers.get('Content-Length') ?? NaN);
      if (Number.isFinite(contentLength) && contentLength > MAX_BACKGROUND_FILE_BYTES) {
        console.warn(
          `[videoPreloader] ${name}: skipping blob cache ` +
          `(${(contentLength / 1048576).toFixed(0)}MB > ${(MAX_BACKGROUND_FILE_BYTES / 1048576).toFixed(0)}MB cap), streaming progressively.`
        );
        entry.controller.abort();
        if (videos.get(key) === entry) videos.delete(key);
        return null;
      }
      // Stream the body so large files report progress instead of hanging
      // silently inside response.blob(). Falls back for mocked responses.
      let blob: Blob;
      const reader = response.body?.getReader();
      if (reader) {
        const chunks: BlobPart[] = [];
        let bytes = 0;
        let lastLog = 0;
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          if (value) {
            chunks.push(value);
            bytes += value.byteLength ?? value.length ?? 0;
          }
          if (bytes > MAX_BACKGROUND_FILE_BYTES) {
            await reader.cancel().catch(() => undefined);
            console.warn(
              `[videoPreloader] ${name}: exceeded blob cache cap mid-download, streaming progressively.`
            );
            if (videos.get(key) === entry) videos.delete(key);
            return null;
          }
          if (bytes - lastLog >= PROGRESS_LOG_BYTES) {
            lastLog = bytes;
            console.info(`[videoPreloader] ${name}: caching … ${(bytes / 1048576).toFixed(1)}MB`);
          }
        }
        const headerType = response.headers.get('Content-Type')?.split(';')[0].trim() ?? '';
        blob = new Blob(chunks, { type: headerType });
        if (response.status === 206) {
          const range = /^bytes 0-(\d+)\/(\d+)$/.exec(response.headers.get('Content-Range') ?? '');
          if (!range || Number(range[1]) + 1 !== Number(range[2]) || blob.size !== Number(range[2])) {
            throw new Error('Video download is incomplete (check the server Content-Range response)');
          }
        }
      } else {
        blob = await response.blob();
        if (response.status === 206) {
          const range = /^bytes 0-(\d+)\/(\d+)$/.exec(response.headers.get('Content-Range') ?? '');
          if (!range || Number(range[1]) + 1 !== Number(range[2]) || blob.size !== Number(range[2])) {
            throw new Error('Video download is incomplete (check the server Content-Range response)');
          }
        }
      }
      if (!blob.size) throw new Error('Video download is empty');
      // Blob URLs lose the file extension. Preserve video MIME types, but give
      // untyped/binary storage responses the same container hint as the URL.
      if (!blob.type || /^(application|binary)\/octet-stream$/i.test(blob.type)) {
        const extension = /\.(mp4|m4v|webm|mov|avi)(?:[?#]|$)/i.exec(fullUrl)?.[1].toLowerCase();
        const mimeTypes: Record<string, string> = {
          mp4: 'video/mp4', m4v: 'video/mp4', webm: 'video/webm',
          mov: 'video/quicktime', avi: 'video/x-msvideo',
        };
        if (extension) blob = blob.slice(0, blob.size, mimeTypes[extension]);
      }
      entry.controller.signal.throwIfAborted();
      // Evicted while downloading (e.g. LRU): drop the orphan, don't resurrect.
      if (videos.get(key) !== entry) {
        const orphan = URL.createObjectURL(blob);
        URL.revokeObjectURL(orphan);
        return null;
      }
      entry.bytes = blob.size;
      entry.objectUrl = URL.createObjectURL(blob);
      trimCache();
      const elapsed = Date.now() - startTime;
      const rate = elapsed > 0 ? (blob.size / 1024 / (elapsed / 1000)).toFixed(0) : '?';
      const slow = elapsed >= SLOW_WARN_MS ? ' (SLOW — consider CDN/preview rendition)' : '';
      console.info(
        `[videoPreloader] ${name}: blob cached ${(blob.size / 1048576).toFixed(1)}MB ` +
        `in ${(elapsed / 1000).toFixed(1)}s (~${rate} KB/s)${slow}`
      );
      return entry.objectUrl;
    } catch (error) {
      // Background-only failure: playback already streams the remote URL, so
      // log and clear the entry for a retry instead of failing the renderer.
      if ((error as Error)?.name !== 'AbortError') {
        console.warn(`[videoPreloader] ${name}: background cache failed (${(error as Error)?.message}), streaming progressively.`);
      }
      if (videos.get(key) === entry) videos.delete(key);
      return null;
    }
  })();
};

const ensureBackgroundFetch = (key: string, fullUrl: string): CachedVideo => {
  const existing = videos.get(key);
  if (existing) {
    videos.delete(key);
    videos.set(key, existing);
    return existing;
  }

  const controller = new AbortController();
  const entry: CachedVideo = {
    controller, bytes: 0, users: 0,
    ready: Promise.resolve(null),
  };
  videos.set(key, entry);
  fetchBlobInBackground(key, fullUrl, entry);
  trimCache();
  return entry;
};

export const acquireVideo = async (
  url: string,
  signal?: AbortSignal
): Promise<HTMLVideoElement> => {
  signal?.throwIfAborted();
  // Caller-owned blob/data URLs are already local; do not copy or revoke them.
  if (/^(blob:|data:)/i.test(url)) {
    const local = document.createElement('video');
    local.crossOrigin = 'anonymous';
    local.preload = 'auto';
    local.playsInline = true;
    local.loop = false;
    local.muted = false;
    local.src = url;
    local.load();
    return local;
  }
  const key = canonicalVideoKey(url);
  const entry = ensureBackgroundFetch(key, url);
  entry.users++;
  try {
    // Yield so a caller abort racing this call takes effect here, without
    // ever cancelling the shared background download (a replacement reuses it).
    await Promise.resolve();
    signal?.throwIfAborted();
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.preload = 'auto';
    video.playsInline = true;
    video.loop = false;
    video.muted = false;
    // Progressive playback: stream the (freshly signed) remote URL now. If a
    // previous visit already warmed the blob cache, play the local blob.
    // This never waits on the network — first frame arrives via Range
    // streaming while the background fetch fills the cache for revisits.
    video.src = entry.objectUrl ?? url;
    // This loads the remote URL progressively (or the local blob on revisit),
    // so playback starts at first frame instead of full download.
    video.load();
    leases.set(video, entry);
    return video;
  } catch (error) {
    entry.users--;
    trimCache();
    throw error;
  }
};

export const releaseVideo = (_url: string, video: HTMLVideoElement): void => {
  video.pause();
  video.removeAttribute('src');
  video.load();
  const entry = leases.get(video);
  if (entry) {
    leases.delete(video);
    entry.users--;
  }
  trimCache();
};

export const preloadVideo = (url: string | null | undefined): void => {
  if (!url || typeof document === 'undefined' || /^(blob:|data:)/i.test(url)) return;
  // Preload and render share the same in-flight background download, keyed by
  // blob path. Cancelling a renderer never cancels that download, and a SAS
  // refresh never restarts it — a replacement immediately reuses it.
  ensureBackgroundFetch(canonicalVideoKey(url), url);
};

/**
 * Drop a *stale prefetch*: aborts the in-flight background download only when
 * nobody uses it and it hasn't warmed yet. Warmed blobs (revisit cache) and
 * entries held by a renderer (`users > 0`) are never touched. Accepts a full
 * URL or an already-canonical key. Used by the scene prefetch queue to free
 * bandwidth for scenes closer to the playhead.
 */
export const cancelBackgroundFetch = (url: string | null | undefined): void => {
  if (!url) return;
  const key = canonicalVideoKey(url);
  const entry = videos.get(key);
  if (!entry || entry.users > 0 || entry.objectUrl) return;
  videos.delete(key);
  entry.controller.abort();
};

/**
 * Await the background blob for a URL (warmed by preload/acquire). Resolves
 * the object URL once cached, or null when there is nothing to wait for (no
 * entry, local URL, skipped oversized file, failed download) or the wait
 * itself times out. Used as a fallback: if progressive metadata waits stall
 * out, the renderer swaps onto the fully-downloaded blob instead of giving up.
 */
export const whenVideoCached = (
  url: string | null | undefined,
  signal?: AbortSignal,
  timeoutMs = 120000
): Promise<string | null> => {
  if (!url || /^(blob:|data:)/i.test(url)) return Promise.resolve(null);
  const entry = videos.get(canonicalVideoKey(url));
  if (!entry) return Promise.resolve(null);
  if (entry.objectUrl) return Promise.resolve(entry.objectUrl);
  return new Promise<string | null>((resolve, reject) => {
    const pending: { timer?: ReturnType<typeof setTimeout> } = {};
    const done = (fn: () => void) => {
      if (pending.timer !== undefined) clearTimeout(pending.timer);
      signal?.removeEventListener('abort', onAbort);
      fn();
    };
    const onAbort = () =>
      done(() => reject(new DOMException('Video load cancelled', 'AbortError')));
    if (signal?.aborted) return onAbort();
    signal?.addEventListener('abort', onAbort, { once: true });
    pending.timer = setTimeout(() => done(() => resolve(null)), timeoutMs);
    entry.ready.then(
      (objectUrl) => done(() => resolve(objectUrl)),
      () => done(() => resolve(null))
    );
  });
};
